/**
 * How long a cached answer stands before a new observer re-asks the api (storefront-fix-spec
 * §4.2). The global default is `Infinity` (main.tsx); a hook that needs anything else says so
 * here, by name, so the policy reads in one place:
 *
 * - Freshness belongs to the data of a deal and to a car's occupancy — a status, a quote, a
 *   calendar — and those hooks keep `staleTime: 0` on purpose (§9.3): a stale calendar sells a
 *   booked day. Zero does not make a person's own deals safe to share: a screen shows the cached
 *   answer while it asks again, and keeps it when the new answer is a refusal — so those reads are
 *   keyed by the session that asked (`QueryKeys.forSession`, app#248).
 * - It does not belong to *who you are*. `/wallet/auth/me`, `/user/auth/me` and the XP balance
 *   were re-asked by every component that mounted on a screen (three times on `/profile` alone),
 *   until the auth rate limit answered 429 (app#156). A session answer may stand because it can
 *   never be served to the wrong person: the session queries are keyed by their token
 *   (`QueryKeys.userFor` / `userMeFor`), so a new sign-in is a new query, and sign-out empties
 *   the cache (`useLogout`, the storage listeners in `useAuth` / `useUserSession`).
 */

/**
 * Live: a deal's status, a car's occupancy, a quote — re-asked by every observer, on purpose.
 * The value is zero; the name says the zero is deliberate, not a leftover.
 */
export const LIVE_STALE_TIME = 0;

/** Session identity: who is signed in, with which wallet or account. */
export const SESSION_STALE_TIME = 5 * 60 * 1000;

/** Reference data that changes with a deploy: the chain registry, the delivery cities. */
export const REFERENCE_STALE_TIME = 5 * 60 * 1000;

/** The XP ledger: changes when a deal closes; a minute is short enough for the balance card, and the ledger screen has Refresh. */
export const XP_STALE_TIME = 60 * 1000;
